import { useRouter } from 'next/router'
import dynamic from 'next/dynamic'

import { HeaderBarLeftAction, HeaderBarTitle } from '@wartek-id/header-bar'
import { Icon } from '@wartek-id/icon'
import { Text } from '@wartek-id/text'

type DesktopLayoutProps = {
  children?: React.ReactNode
  submenu?: React.ReactNode
}

const PELATIHAN_MANDIRI_PATH = '/pelatihan-mandiri'
const SUB_MENU_TEMPLATE = {
  FOR_YOU: dynamic(() => import('app/PelatihanMandiriPage/pages/ForYou'), {
    ssr: false,
  }),
  TOPIK: dynamic(() => import('app/PelatihanMandiriPage/pages/TopicList'), {
    ssr: false,
  }),
  AKSI_NYATA: dynamic(
    () =>
      import('app/PelatihanMandiriPage/partials/AksiNyata/AksiNyataList').then(
        (template) => template.AksiNyataList
      ),
    {
      ssr: false,
    }
  ),
  MODUL: dynamic(
    () =>
      import('app/PelatihanMandiriPage/ModulPage/ModulDetail/index.desktop'),
    {
      ssr: false,
    }
  ),
}

export const DesktopLayout = ({ children, submenu }: DesktopLayoutProps) => {
  const router = useRouter()
  const { pathname, query } = router

  // TODO: adjust this
  const sideMenu = [
    {
      label: 'Seri Pelatihan',
      icon: 'book',
      href: '/pelatihan-mandiri',
      active:
        pathname === '/pelatihan-mandiri' ||
        pathname.includes('/pelatihan-mandiri/seri-pelatihan') ||
        query.from === 'learning-series',
    },
    {
      label: 'Topik',
      icon: 'topic',
      href: '/pelatihan-mandiri/daftar-topik',
      active:
        pathname === '/pelatihan-mandiri/daftar-topik' ||
        pathname === `${PELATIHAN_MANDIRI_PATH}/modul/[modul]` ||
        pathname === `${PELATIHAN_MANDIRI_PATH}/text/[id]` ||
        pathname === `${PELATIHAN_MANDIRI_PATH}/video/[id]` ||
        pathname === `${PELATIHAN_MANDIRI_PATH}/quiz/[id]` ||
        pathname === `${PELATIHAN_MANDIRI_PATH}/refleksi/[id]` ||
        (pathname.includes('/pelatihan-mandiri/topik/[topik]') &&
          query.from !== 'learning-series'),
    },
    {
      label: 'Webinar',
      icon: 'live_tv',
      href: '/pelatihan-mandiri/webinar',
      active: pathname.includes('/pelatihan-mandiri/webinar'),
    },
    {
      label: 'Aksi nyata',
      icon: 'batch_prediction',
      href: '/pelatihan-mandiri/daftar-aksi-nyata',
      active:
        pathname === '/pelatihan-mandiri/daftar-aksi-nyata' ||
        pathname.includes('/pelatihan-mandiri/aksi-nyata'),
    },
  ]

  return (
    <div className="flex flex-col h-screen w-full">
      <div className="fixed w-full p-4 flex flex-row items-center bg-white z-20">
        <HeaderBarLeftAction
          icon={
            <Icon as="i" color="default">
              arrow_back
            </Icon>
          }
          onClick={async () => {
            await router.push('/home')
          }}
        />
        <HeaderBarTitle>Merdeka Mengajar</HeaderBarTitle>
      </div>
      {/* sidebar */}
      <div className="flex flex-row bg-gray-10 overflow-hidden h-full">
        <div className="flex flex-col w-64 border-r border-gray-200 mt-[80px] px-4">
          {sideMenu.map((item, index) => (
            <div
              key={`sidemenu-${index}`}
              className={`flex flex-row items-center p-2 mt-4 rounded-lg cursor-pointer hover:bg-gray-5 ${
                item.active ? 'bg-gray-20' : ''
              }`}
              onClick={async () => {
                await router.push(item.href)
              }}
            >
              <Icon
                as="i"
                color={item.active ? 'informational' : 'default'}
                className="mr-4"
              >
                {item.icon}
              </Icon>
              <Text
                color={item.active ? 'primary' : 'default'}
                variant="heading-sm"
              >
                {item.label}
              </Text>
            </div>
          ))}
        </div>

        {/* submenu */}
        <div className="overflow-y-scroll w-[380px]">
          <main className="mt-8">{submenu}</main>
        </div>

        {/* content */}
        <div className="flex-1 overflow-y-scroll p-2 max-w-6xl mx-auto md:px-8 lg:px-16">
          <main className="mt-[80px]">{children && children}</main>
        </div>
      </div>
    </div>
  )
}

export const useDesktopSubmenuHook = (isDesktop) => {
  const router = useRouter()
  const { pathname } = router
  const { id, from, menuId, topik } = router.query

  if (!isDesktop) return null

  const SubMenuPageMap = [
    {
      path: `${PELATIHAN_MANDIRI_PATH}/seri-pelatihan/[id]`,
      components: (
        <div key="seri-pelatihan-dekstop-submenu">
          <SUB_MENU_TEMPLATE.FOR_YOU
            isDesktop={true}
            currentId={id as string}
          />
        </div>
      ),
    },
    {
      path: `${PELATIHAN_MANDIRI_PATH}/topik/[topik]`,
      components:
        menuId && from === 'learning-series' ? (
          <div key="topik-desktop-submenu">
            <SUB_MENU_TEMPLATE.FOR_YOU
              isDesktop={true}
              currentId={menuId as string}
            />
          </div>
        ) : (
          <div key="topik-desktop-submenu">
            <SUB_MENU_TEMPLATE.TOPIK
              isDesktop={true}
              currentId={topik as string}
            />
          </div>
        ),
    },
    {
      path: `${PELATIHAN_MANDIRI_PATH}/daftar-aksi-nyata`,
      components: (
        <div key="daftar-aksi-nyata-desktop-submenu">
          <SUB_MENU_TEMPLATE.AKSI_NYATA isDesktop={true} />
        </div>
      ),
    },
    {
      path: `${PELATIHAN_MANDIRI_PATH}/aksi-nyata/[id]`,
      components: (
        <div key="aksi-nyata-desktop-submenu">
          <SUB_MENU_TEMPLATE.AKSI_NYATA isDesktop={true} />
        </div>
      ),
    },
    {
      path: [
        `${PELATIHAN_MANDIRI_PATH}/modul/[modul]`,
        `${PELATIHAN_MANDIRI_PATH}/text/[id]`,
        `${PELATIHAN_MANDIRI_PATH}/video/[id]`,
        `${PELATIHAN_MANDIRI_PATH}/quiz/[id]`,
        `${PELATIHAN_MANDIRI_PATH}/refleksi/[id]`,
      ],
      components: (
        <div key="modul-desktop-submenu">
          <SUB_MENU_TEMPLATE.MODUL />
        </div>
      ),
    },
  ]

  const currentSubMenu = SubMenuPageMap.map((subMenu) => {
    if (typeof subMenu.path === 'string') {
      if (pathname.includes(subMenu.path)) {
        return subMenu.components
      }
    }

    if (Array.isArray(subMenu.path)) {
      if (subMenu.path.some((path) => pathname.includes(path))) {
        return subMenu.components
      }
    }
  })

  return currentSubMenu
}

export const WithDesktopLayout = ({ children }) => {
  const currentSubMenu = useDesktopSubmenuHook(true)

  return <DesktopLayout submenu={currentSubMenu}>{children}</DesktopLayout>
}

export const WithLayoutListener = ({ children, isDesktop }) => {
  if (isDesktop) {
    return <WithDesktopLayout>{children}</WithDesktopLayout>
  }

  return children
}
